import { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import SolutionsSection from "@/components/SolutionsSection";
import RightsSection from "@/components/RightsSection";
import PricingSection from "@/components/PricingSection";
import SubstackSection from "@/components/SubstackSection";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("");
  const sectionsRef = useRef({});

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        setContent(response.data);
      } catch (e) {
        console.error("Failed to fetch content:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  // IntersectionObserver for active nav state
  const registerSection = useCallback((id, el) => {
    sectionsRef.current[id] = el;
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );

    Object.values(sectionsRef.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [content]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground font-mono tracking-wider uppercase">Loading</p>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Unable to load content. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="bottom-right" richColors />
      <Navbar activeSection={activeSection} links={content.footer?.links || []} />
      <main>
        <HeroSection data={content.hero} registerSection={registerSection} />
        <SolutionsSection data={content.solutions} registerSection={registerSection} />
        <RightsSection data={content.rights} registerSection={registerSection} />
        <PricingSection data={content.pricing} registerSection={registerSection} />
        <SubstackSection data={content.substack} registerSection={registerSection} />
      </main>
      <Footer data={content.footer} />
    </div>
  );
}

export default App;
